/**
 * I18n Initializer Component
 * Ensures i18n is ready before rendering the app
 */

import React, { useState, useEffect, ReactNode } from 'react';
import { View, ActivityIndicator, StyleSheet } from 'react-native';
import { I18nextProvider } from 'react-i18next';
import i18n from '../i18n/i18n.config';

interface I18nInitializerProps {
  children: ReactNode;
}

/**
 * Wrapper component that ensures i18n is initialized before rendering children
 */
export const I18nInitializer: React.FC<I18nInitializerProps> = ({ children }) => {
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    const checkI18nReady = async () => {
      console.log('[I18nInitializer] Checking i18n readiness...');

      // Wait for i18n to be initialized
      if (i18n.isInitialized) {
        console.log('[I18nInitializer] ✅ i18n is already initialized');
        console.log('[I18nInitializer] Current language:', i18n.language);
        console.log('[I18nInitializer] Available languages:', Object.keys(i18n.store.data));
        setIsReady(true);
        return;
      }

      // Wait for initialization event
      const onInitialized = () => {
        console.log('[I18nInitializer] ✅ i18n initialized event received');
        console.log('[I18nInitializer] Current language:', i18n.language);
        setIsReady(true);
      };

      i18n.on('initialized', onInitialized);

      // Timeout fallback (should not happen)
      const timeout = setTimeout(() => {
        console.warn('[I18nInitializer] ⚠️ i18n initialization timeout, proceeding anyway');
        setIsReady(true);
      }, 3000);

      return () => {
        i18n.off('initialized', onInitialized);
        clearTimeout(timeout);
      };
    };

    checkI18nReady();
  }, []);

  if (!isReady) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#e94560" />
      </View>
    );
  }

  return (
    <I18nextProvider i18n={i18n}>
      {children}
    </I18nextProvider>
  );
};

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#0B0F14',
    minHeight: '100vh' as any,
  },
});

export default I18nInitializer;